import { useEffect, useState } from "react";
import { Store, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, StatCard, DateNav, Money, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST, inr0 } from "@/lib/format";

const SaleForm = ({ products, date, onSaved }) => {
  const [stall, setStall] = useState(localStorage.getItem("bf_stall") || "");
  const [qty, setQty] = useState({});
  const [mode, setMode] = useState("cash");
  const [busy, setBusy] = useState(false);
  const total = products.reduce((s, p) => s + (+qty[p.id] || 0) * p.price, 0);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/stall-sales", { date, stall, mode, items: products.map((p) => ({ product_id: p.id, qty: +qty[p.id] || 0 })) });
      localStorage.setItem("bf_stall", stall); setQty({}); toast.success(`Sale of ${inr0(total)} recorded`); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="stall-sale-form">
      <div className="font-display text-lg font-semibold">New stall sale</div>
      <Input value={stall} onChange={(e) => setStall(e.target.value)} placeholder="Stall location (e.g. Whitefield Sunday market)" data-testid="stall-name-input" />
      {products.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-3 text-sm">
          <span>{p.name} <span className="text-muted-foreground">· {inr0(p.price)}</span></span>
          <Input type="number" min="0" value={qty[p.id] || ""} onChange={(e) => setQty({ ...qty, [p.id]: e.target.value })} className="h-9 w-20" data-testid={`stall-qty-${p.id}`} />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <NativeSelect className="w-28" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="stall-mode-select"><option value="cash">Cash</option><option value="upi">UPI</option></NativeSelect>
        <Button disabled={busy || !stall.trim() || total <= 0} onClick={save} className="flex-1 rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="stall-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Record sale ${inr0(total)}`}</Button>
      </div>
    </div>
  );
};

export default function StallSales() {
  const [date, setDate] = useState(todayIST());
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const load = () => api.get("/stall-sales", { params: { start: date, end: date } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active !== false))); }, []);
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (s) => { if (window.confirm("Delete this sale?")) { await api.delete(`/stall-sales/${s.id}`); load(); } };
  return (
    <div data-testid="stall-sales-page">
      <PageHeader eyebrow="B2P · brand stalls" title="Stall sales" subtitle="Instant cash / UPI sales at promotion stalls around Bangalore. Not added to any customer's credit.">
        <DateNav value={date} onChange={setDate} />
      </PageHeader>
      {data && <div className="mb-6 grid gap-4 md:grid-cols-3">
        <StatCard label="Stall sales" value={inr0(data.total)} icon={Store} testid="stall-total" />
        <StatCard label="Cash" value={inr0(data.cash)} testid="stall-cash" />
        <StatCard label="UPI" value={inr0(data.upi)} testid="stall-upi" />
      </div>}
      <div className="grid gap-6 lg:grid-cols-2">
        <SaleForm products={products} date={date} onSaved={load} />
        <div className="space-y-2" data-testid="stall-sales-list">
          {data?.stalls.map((g) => <div key={g.stall} className="rounded-xl bg-[#FBF9F4] px-4 py-2 text-sm" data-testid="stall-summary">{g.stall}: <b>{inr0(g.total)}</b> · {g.packs} packs · {g.sales} sales</div>)}
          {data?.rows.length === 0 && <div className="rounded-2xl border border-dashed border-[#E7E2D6] p-8 text-center text-sm text-muted-foreground" data-testid="stall-empty">No stall sales on this day</div>}
          {data?.rows.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-2xl border border-[#E7E2D6] bg-white p-3 text-sm" data-testid={`stall-sale-${s.id}`}>
              <div className="flex-1"><div className="font-semibold">{s.stall} <span className="text-xs font-normal uppercase text-muted-foreground">{s.mode}</span></div><div className="text-xs text-muted-foreground">{s.items.map((i) => `${i.name} × ${i.qty}`).join(", ")} · by {s.by}</div></div>
              <Money value={s.total} className="font-semibold" />
              <Button size="icon" variant="ghost" onClick={() => remove(s)} aria-label="Delete sale" data-testid={`stall-delete-${s.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
